import React, { useEffect, useRef, useState } from "react";
import { HttpStatusCode } from "axios";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { ContactInfo, Title } from "~/components";
import { ROUTES } from "~/router/constants/routes";
import { AuthLayout } from "~/screens/Auth/Layout/AuthLayout";
import type { PersonalInformationFormData } from "~/screens/Scheduling/PersonalInformation/validation/schema";
import {
  BOOKING_FLOW_EVENTS,
  useBookingFlowEvents,
  useScheduleVisitMutation,
} from "~/services";
import { usePageTitle, useUsers } from "~/shared";
import {
  useAuthStore,
  useBookingFlowStore,
  usePatientStore,
  useUserStore,
} from "~/stores";
import { useNirvanaStore } from "~/stores/use-nirvana-store";
import { errorToast, icons } from "~/ui";
import { getFieldErrorMessages, getVisitTypeTitle, tw } from "~/utils";
import { AppointmentDetailsCard, PersonalInformationForm } from "./components";
import { LeaveConfirmationModal } from "./components/leave-confirmation-modal";
import { useVisitType } from "./hooks";
import { buildPatientVisitData } from "./utils/form-data-processor";

export const PersonalInformation = () => {
  usePageTitle("Patient Information");

  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const { visitType } = useVisitType();
  const {
    patientVisitData: storePatientData,
    clearPatientVisitData,
    appointmentDetails,
  } = usePatientStore();
  const stateId = useUserStore((state) => state.state);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn());
  const { getCurrentUser } = useUsers();
  const { data: currentUser, isLoading: isLoadingCurrentUser } =
    getCurrentUser(isLoggedIn);
  const userEmail = currentUser?.email ?? "";
  const setHasFileSizeError = usePatientStore(
    (state) => state.setHasFileSizeError,
  );
  const clearBookingFlow = useBookingFlowStore((state) => state.clear);
  const bookingFlow = useBookingFlowStore((state) => state.flow);
  const nirvanaInsuranceKey = useNirvanaStore(
    (state) => state.result?.insuranceCacheKey,
  );
  const isSubmittingRef = useRef(false);

  const handleBackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowModal(true);
  };

  const handleLeave = () => {
    setShowModal(false);
    isLoggedIn
      ? navigate(ROUTES.hubMeetProviderAppointments)
      : navigate(ROUTES.schedulingEmailInput);
  };

  const handleStay = () => {
    setShowModal(false);
  };

  const { trackTimeEvent } = useBookingFlowEvents();

  useEffect(() => {
    trackTimeEvent(BOOKING_FLOW_EVENTS.PATIENT_INFO_PAGE_VIEWED_AT);
  }, []);

  const scheduleVisitMutation = useScheduleVisitMutation({
    onSuccess: (data) => {
      clearBookingFlow();
      const route = isLoggedIn
        ? ROUTES.hubMeetProviderConfirmed
        : ROUTES.schedulingVisitConfirmed;
      navigate(route, {
        state: {
          appointment: data,
          email: storePatientData?.patientEmail || userEmail,
        },
      });

      clearPatientVisitData();
      setHasFileSizeError(false);
    },
    onError: (error: any) => {
      if (error?.response?.status === HttpStatusCode.PayloadTooLarge) {
        setHasFileSizeError(true);
      } else {
        const fieldsErrorMessages = getFieldErrorMessages(error);
        errorToast(fieldsErrorMessages ?? error);
      }
    },
  });

  if (!stateId) return <Navigate to={ROUTES.getStarted} />;

  const handleFormSubmit = async (data: PersonalInformationFormData) => {
    if (isSubmittingRef.current) {
      return;
    }

    if (!bookingFlow) {
      errorToast(
        "We could not process your request. Please reload the page and try again.",
      );
      return;
    }

    isSubmittingRef.current = true;

    const patientVisitData = buildPatientVisitData(
      data,
      visitType,
      storePatientData,
      userEmail,
      nirvanaInsuranceKey,
    );

    scheduleVisitMutation.mutate(
      {
        bookingFlowId: bookingFlow.id,
        visitData: patientVisitData,
      },
      {
        onSettled: () => {
          isSubmittingRef.current = false;
        },
      },
    );
  };

  const visitTitle = getVisitTypeTitle(visitType);

  return (
    <AuthLayout>
      <main className="mx-auto max-w-6xl justify-center md:grid md:grid-cols-2 md:items-start md:gap-x-8">
        <div className="mx-auto mb-8 md:sticky md:top-8 md:max-w-sm md:self-start">
          <Link
            to={"#"}
            className={tw(
              "text-violet left-0 mb-4 flex items-center gap-2 font-medium",
            )}
            role="link"
            onClick={handleBackClick}
          >
            <div className="border-violet text-violet rounded-full border p-0.5">
              <icons.outline.ArrowLongLeftIcon className="w-6" />
            </div>
            Back
          </Link>
          <Title className="mb-6 font-light">
            Confirm your {visitTitle.toLowerCase()}
          </Title>

          <AppointmentDetailsCard appointmentDetails={appointmentDetails} />
          <ContactInfo className="md:hidden" />
        </div>

        <PersonalInformationForm
          visitType={visitType}
          onSubmit={handleFormSubmit}
          isLoading={scheduleVisitMutation.isPending || isLoadingCurrentUser}
        />
      </main>

      <LeaveConfirmationModal
        show={showModal}
        onClose={handleStay}
        onLeave={handleLeave}
      />
    </AuthLayout>
  );
};
